import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import { ThemeContext } from "../utils/ThemeContext";

const COLORS = [
  "#4F46E5",
  "#7C3AED",
  "#DB2777",
  "#F43F5E",
  "#F97316",
  "#F59E0B",
  "#10B981",
  "#0D9488",
  "#06B6D4",
  "#3B82F6",
];

const ThemeSettings = () => {
  const { theme, updateTheme } = useContext(ThemeContext);

  const defaultTheme = {
    sidebarColor: "#2c3e50",
    appBarColor: "#2f63ad",
  };

  const [selectedSidebar, setSelectedSidebar] = useState(defaultTheme.sidebarColor);
  const [selectedAppbar, setSelectedAppbar] = useState(defaultTheme.appBarColor);

  useEffect(() => {
    const storedTheme = localStorage.getItem("customTheme");
    if (storedTheme) {
      const parsed = JSON.parse(storedTheme);
      setSelectedSidebar(parsed.sidebarColor);
      setSelectedAppbar(parsed.appBarColor);
      updateTheme(parsed);
    }
  }, []);

  const handleSidebarColor = (color) => {
    setSelectedSidebar(color);
    const newTheme = { ...theme, sidebarColor: color };
    updateTheme(newTheme);
    localStorage.setItem("customTheme", JSON.stringify(newTheme));
  };

  const handleAppbarColor = (color) => {
    setSelectedAppbar(color);
    const newTheme = { ...theme, appBarColor: color };
    updateTheme(newTheme);
    localStorage.setItem("customTheme", JSON.stringify(newTheme));
  };

  const handleReset = () => {
    setSelectedSidebar(defaultTheme.sidebarColor);
    setSelectedAppbar(defaultTheme.appBarColor);
    updateTheme(defaultTheme);
    localStorage.removeItem("customTheme");
  };

  const pageCardSx = {
    maxWidth: 860,
    mx: "auto",
    mt: 1,
    p: { xs: 2, md: 3 },
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    backgroundColor: "rgba(255,255,255,0.96)",
    p: { xs: 2, md: 2.5 },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    borderColor: "rgba(47,99,173,0.25)",
    color: "#2f63ad",
    px: 2.25,
  };

  const ColorBlock = ({ color, selected, onClick }) => (
    <Box
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        backgroundColor: color,
        cursor: "pointer",
        border: selected ? "3px solid #223142" : "1px solid rgba(0,0,0,0.08)",
        boxShadow: selected
          ? "0 0 0 3px rgba(47,99,173,0.14)"
          : "0 2px 8px rgba(0,0,0,0.08)",
        transform: selected ? "scale(1.06)" : "scale(1)",
        transition: "all 0.18s ease",
        "&:hover": {
          transform: "scale(1.06)",
        },
      }}
    />
  );

  const PreviewSwatch = ({ label, color, icon }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "14px",
        border: "1px solid rgba(47,99,173,0.08)",
        backgroundColor: "rgba(255,255,255,0.96)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ color: "#2f63ad", display: "flex", alignItems: "center" }}>
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ color: "#6b778c", fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>

      <Box
        sx={{
          height: 42,
          borderRadius: "10px",
          backgroundColor: color,
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      />

      <Typography
        variant="body2"
        sx={{
          mt: 1,
          color: "#223142",
          fontWeight: 600,
          fontFamily: "monospace",
        }}
      >
        {color}
      </Typography>
    </Paper>
  );

  return (
    <Paper elevation={0} sx={pageCardSx}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Theme Settings
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Customize the sidebar and top bar colors used throughout the app.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 2.5 }}>
        <Paper elevation={0} sx={sectionCardSx}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Sidebar Color
          </Typography>

          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Choose the main color used for the sidebar.
          </Typography>

          <Grid container spacing={1.25}>
            {COLORS.map((color) => (
              <Grid item key={color}>
                <ColorBlock
                  color={color}
                  selected={color === selectedSidebar}
                  onClick={() => handleSidebarColor(color)}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper elevation={0} sx={sectionCardSx}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Top Bar Color
          </Typography>

          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Choose the main color used for the top navigation bar.
          </Typography>

          <Grid container spacing={1.25}>
            {COLORS.map((color) => (
              <Grid item key={color}>
                <ColorBlock
                  color={color}
                  selected={color === selectedAppbar}
                  onClick={() => handleAppbarColor(color)}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PreviewSwatch
              label="Sidebar Preview"
              color={selectedSidebar}
              icon={<ViewSidebarIcon fontSize="small" />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <PreviewSwatch
              label="Top Bar Preview"
              color={selectedAppbar}
              icon={<WebAssetIcon fontSize="small" />}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            sx={secondaryButtonSx}
          >
            Reset to Default
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ThemeSettings;
